import React, { useState, useEffect } from 'react';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import {
  Square,
  Button,
  SvgX,
  Heading,
  Copy,
} from '@grubhubprod/gh-cookbook-react';
import { Modal } from 'src/components/Modal/Modal';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { SignupProgressTracker } from 'src/components/common/features/Signup/components/SignupProgressTracker/SignupProgressTracker';
import { getSignupSteps } from 'src/components/selfService/features/Signup/context/SignupStatusProvider/SignupStatusProvider';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { useMealCreditEntityName } from 'src/components/selfService/common/hooks/useMealCreditEntityName';
import { capitalize } from 'src/utilities/string-utils';

interface IWelcomeBackModalProps {
  onClose: () => void;
}

export function WelcomeBackModal({
  onClose,
}: IWelcomeBackModalProps): JSX.Element {
  const { user } = useAuth();
  const { MEAL_CREDIT_ENTITY_NAME: entityName } = useMealCreditEntityName();
  const [modalSignupSteps] = useState(
    getSignupSteps(true, capitalize(entityName.singular))
  );
  const { sendTealiumEvent, sendTealiumPageView } = useAnalytics();

  useEffect(() => {
    sendTealiumPageView({
      pageGroup: 'corporate self service - sign up flow',
      pageName: 'welcome back',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function closeModal() {
    sendTealiumEvent({
      eventcategory: 'self-service sign up_welcome back modal',
      eventaction: 'cancel_cta',
    });
    onClose();
  }

  function continueSetup() {
    sendTealiumEvent({
      eventcategory: 'self-service sign up_welcome back modal',
      eventaction: 'continue account setup_cta',
    });
    onClose();
  }

  const firstName = get(user, 'first_name') || '';
  return (
    <Modal size="sm">
      <Square as="header" dimension="2" width="100%">
        <Button variant="tertiary" type="button" onClick={closeModal}>
          <SvgX size="24px" data-testid="close-icon" />
        </Button>
        <Square inset="md">
          <Heading variant="h1">Welcome back, {firstName}!</Heading>
          <Copy variant="body">Let's finish what you started</Copy>
        </Square>
      </Square>
      <Square inset="lg">
        <SignupProgressTracker steps={modalSignupSteps} isInWelcomeModal />
      </Square>
      <Square as="footer" dimension="2" inset="md">
        <Button variant="primary" buttonSize="medium" onClick={continueSetup}>
          Continue setting up account
        </Button>
      </Square>
    </Modal>
  );
}
